import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Switch from '@mui/material/Switch';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';
import PinOutlinedIcon from '@mui/icons-material/PinOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import TableViewOutlinedIcon from '@mui/icons-material/TableViewOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
// import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';

import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { BASE_URL } from '../../api/axios';
import './DeliveryProfile.css';

function getInitials(name) {
  if (!name) return 'DR';
  const parts = String(name).trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return parts[0].slice(0, 2).toUpperCase();
}

function formatLocation(loc) {
  if (!loc) return 'Not set';
  const text = String(loc).trim();
  if (/^-?\d+(\.\d+)?,\s*-?\d+(\.\d+)?$/.test(text)) {
    const [lat, lng] = text.split(',').map((v) => Number(v.trim()));
    if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
      return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    }
  }
  return text;
}

const VEHICLE_TYPES = [
  'Bicycle / Scooter',
  'Motorcycle',
  'Sedan Car / SUV',
  'Three-Wheeler M.C.',
  'Small Pickup Truck',
  'Light Pickup Truck / Cargo Van',
];

const SUPPORT_EMAIL = 'sales.shop@jcgn.org';

function Profile() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toggling, setToggling] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [photoFile, setPhotoFile] = useState(null);
  const [editForm, setEditForm] = useState({});

  const userId = localStorage.getItem('userId');
  const userRole = localStorage.getItem('userRole') || 'deliver';

  const fetchProfile = useCallback(async () => {
    if (!userId) {
      setError('Please sign in to view your profile.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${BASE_URL}deliver/getbyid/${userId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setUser(res.data?.data || res.data);
    } catch (err) {
      console.error('Profile fetch error:', err);
      setError(
        err.response?.status === 404
          ? 'No delivery profile is linked to this account. Please contact support at sales.shop@jcgn.org.'
          : 'Could not load your profile. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleStatusToggle = async () => {
    if (!user || toggling) return;
    setToggling(true);
    const newStatus = !user.deliverstatus;
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BASE_URL}deliver/${userId}/status`,
        { deliverStatus: newStatus },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      setUser((prev) => ({ ...prev, deliverstatus: newStatus }));
      setSnack({
        open: true,
        message: newStatus
          ? 'You are now available for deliveries'
          : 'You are marked as unavailable',
        severity: 'success',
      });
    } catch (err) {
      console.error(err);
      setSnack({
        open: true,
        message: 'Failed to update availability',
        severity: 'error',
      });
    } finally {
      setToggling(false);
    }
  };

  const startEditing = () => {
    setEditForm({
      delivername: user.delivername || '',
      deliverphone: user.deliverphone || '',
      delivervehicle: user.delivervehicle || '',
      delivervehiclemake: user.delivervehiclemake || '',
      delivervehiclemodel: user.delivervehiclemodel || '',
      delivervehiclecolor: user.delivervehiclecolor || '',
      delivervehiclenumber: user.delivervehiclenumber || '',
      taxidriverid: user.taxidriverid || '',
      deliverlocation: user.deliverlocation || '',
    });
    setPhotoFile(null);
    setEditing(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async () => {
    if (saving) return;
    if (!editForm.delivername?.trim() || !editForm.deliverphone?.trim()) {
      setSnack({ open: true, message: 'Full name and phone are required', severity: 'error' });
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const data = new FormData();
      Object.entries(editForm).forEach(([k, v]) => {
        if (v !== undefined && v !== null) data.append(k, v);
      });
      if (photoFile) data.append('photo', photoFile);

      const res = await axios.put(`${BASE_URL}deliver/${userId}`, data, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const updated = res.data?.data;
      if (updated && !Array.isArray(updated)) {
        setUser((prev) => ({ ...prev, ...(updated.toJSON ? updated.toJSON() : updated) }));
      }
      setEditing(false);
      setPhotoFile(null);
      setSnack({ open: true, message: 'Profile updated successfully', severity: 'success' });
      fetchProfile();
    } catch (err) {
      console.error(err);
      setSnack({
        open: true,
        message: err.response?.data?.message || 'Failed to update profile',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const isActive = Boolean(user?.deliverstatus);
  const vehicleLabel = user
    ? [user.delivervehicle, user.delivervehiclecolor, user.delivervehiclenumber]
        .filter(Boolean)
        .join(' · ') || 'Not set'
    : '—';

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <div className="dp-profile-page">
        {loading && (
          <div className="dp-center-state">
            <div className="dp-spinner" />
            <p>Loading your profile…</p>
          </div>
        )}

        {!loading && error && (
          <div className="dp-center-state">
            <p>{error}</p>
            <button type="button" className="dp-btn dp-btn-primary" onClick={fetchProfile}>
              Retry
            </button>
          </div>
        )}

        {!loading && !error && user && (
          <>
            <section className="dp-hero">
              <div className="dp-hero-inner">
                <div className="dp-avatar-wrap">
                  {user.deliverphoto ? (
                    <img className="dp-avatar" src={user.deliverphoto} alt={user.delivername || 'Delivery staff'} />
                  ) : (
                    <div className="dp-avatar" aria-hidden="true">
                      {getInitials(user.delivername)}
                    </div>
                  )}
                  <span
                    className={`dp-status-dot ${isActive ? 'active' : ''}`}
                    title={isActive ? 'Available' : 'Unavailable'}
                  />
                </div>

                <div className="dp-hero-text">
                  <div className="dp-hero-eyebrow notranslate" translate="no">
                    JCGN Delivery Partner
                  </div>
                  <h1 className="dp-hero-name">{user.delivername || 'Delivery Staff'}</h1>
                  <p className="dp-hero-role">
                    Trusted local delivery partner · {userRole === 'deliver' ? 'Driver' : userRole}
                  </p>
                  <div className="dp-badge-row">
                    <span className={`dp-badge ${isActive ? 'active' : 'inactive'}`}>
                      {isActive ? '● Available' : '● Unavailable'}
                    </span>
                    <span className="dp-badge">ID #{user.id}</span>
                    {user.delivervehicle && (
                      <span className="dp-badge">{user.delivervehicle}</span>
                    )}
                  </div>

                  <div className="dp-hero-actions">
                    <button type="button" className="dp-btn dp-btn-primary" onClick={startEditing}>
                      <EditOutlinedIcon fontSize="small" />
                      Edit Profile
                    </button>
                    <Link to="/tables" className="dp-btn dp-btn-primary">
                      <TableViewOutlinedIcon fontSize="small" />
                      My Orders
                    </Link>
                    <Link to="/billing" className="dp-btn dp-btn-ghost">
                      <ReceiptLongOutlinedIcon fontSize="small" />
                      Verify OTP
                    </Link>
                  </div>
                </div>
              </div>
            </section>

            {editing && (
              <div className="dp-card" style={{ marginBottom: '1rem' }}>
                <h2 className="dp-card-title">
                  <EditOutlinedIcon />
                  Edit profile
                </h2>
                <div className="dp-edit-grid">
                  <TextField
                    name="delivername"
                    label="Full Name (as on national ID)"
                    value={editForm.delivername}
                    onChange={handleEditChange}
                    size="small"
                    fullWidth
                    required
                  />
                  <TextField
                    name="deliverphone"
                    label="Phone (with country code, e.g. +1 ...)"
                    value={editForm.deliverphone}
                    onChange={handleEditChange}
                    size="small"
                    fullWidth
                    required
                  />
                  <Select
                    name="delivervehicle"
                    value={editForm.delivervehicle}
                    onChange={handleEditChange}
                    size="small"
                    fullWidth
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Vehicle Type
                    </MenuItem>
                    {VEHICLE_TYPES.map((v) => (
                      <MenuItem key={v} value={v}>
                        {v}
                      </MenuItem>
                    ))}
                  </Select>
                  <TextField
                    name="delivervehiclecolor"
                    label="Vehicle Color"
                    value={editForm.delivervehiclecolor}
                    onChange={handleEditChange}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    name="delivervehiclemake"
                    label="Vehicle Make (e.g. Toyota)"
                    value={editForm.delivervehiclemake}
                    onChange={handleEditChange}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    name="delivervehiclemodel"
                    label="Vehicle Model (e.g. Camry LE)"
                    value={editForm.delivervehiclemodel}
                    onChange={handleEditChange}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    name="delivervehiclenumber"
                    label="Vehicle Plate Number"
                    value={editForm.delivervehiclenumber}
                    onChange={handleEditChange}
                    size="small"
                    fullWidth
                    required
                  />
                  <TextField
                    name="taxidriverid"
                    label="Taxi Driver Membership ID (if applicable)"
                    value={editForm.taxidriverid}
                    onChange={handleEditChange}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    name="deliverlocation"
                    label="Base Location"
                    value={editForm.deliverlocation}
                    onChange={handleEditChange}
                    size="small"
                    fullWidth
                  />
                  <label className="dp-photo-input">
                    <PhotoCameraOutlinedIcon fontSize="small" />
                    {photoFile ? photoFile.name : 'Upload staff photo (helps customers identify you)'}
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) => setPhotoFile(e.target.files?.[0] || null)}
                    />
                  </label>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                  <button
                    type="button"
                    className="dp-btn dp-btn-primary"
                    onClick={handleSaveProfile}
                    disabled={saving}
                  >
                    {saving ? 'Saving…' : 'Save changes'}
                  </button>
                  <button
                    type="button"
                    className="dp-btn dp-btn-ghost"
                    onClick={() => setEditing(false)}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="dp-stats">
              <div className="dp-stat-card">
                <div className="dp-stat-icon blue">
                  <PersonOutlineIcon />
                </div>
                <div className="dp-stat-label">Driver</div>
                <div className="dp-stat-value">{user.delivername || '—'}</div>
              </div>
              <div className="dp-stat-card">
                <div className="dp-stat-icon red">
                  <DirectionsCarOutlinedIcon />
                </div>
                <div className="dp-stat-label">Vehicle</div>
                <div className="dp-stat-value">{user.delivervehicle || 'Not set'}</div>
              </div>
              <div className="dp-stat-card">
                <div className="dp-stat-icon gold">
                  <PinOutlinedIcon />
                </div>
                <div className="dp-stat-label">Plate</div>
                <div className="dp-stat-value">{user.delivervehiclenumber || 'Not set'}</div>
              </div>
              <div className="dp-stat-card">
                <div className="dp-stat-icon navy">
                  <LocalShippingOutlinedIcon />
                </div>
                <div className="dp-stat-label">Status</div>
                <div className="dp-stat-value">{isActive ? 'Ready to deliver' : 'Off duty'}</div>
              </div>
            </div>

            <div className="dp-grid">
              <div className="dp-card">
                <h2 className="dp-card-title">
                  <PersonOutlineIcon />
                  Contact &amp; account
                </h2>
                <div className="dp-info-list">
                  <div className="dp-info-row">
                    <div className="dp-info-icon">
                      <EmailOutlinedIcon fontSize="small" />
                    </div>
                    <div>
                      <div className="dp-info-label">Email</div>
                      <div className="dp-info-value">
                        <a href={`mailto:${user.deliveremail}`}>{user.deliveremail}</a>
                      </div>
                    </div>
                  </div>
                  <div className="dp-info-row">
                    <div className="dp-info-icon">
                      <PhoneOutlinedIcon fontSize="small" />
                    </div>
                    <div>
                      <div className="dp-info-label">Phone</div>
                      <div className="dp-info-value">
                        <a href={`tel:${user.deliverphone}`}>{user.deliverphone}</a>
                      </div>
                    </div>
                  </div>
                  <div className="dp-info-row">
                    <div className="dp-info-icon">
                      <LocationOnOutlinedIcon fontSize="small" />
                    </div>
                    <div>
                      <div className="dp-info-label">Base location</div>
                      <div className="dp-info-value">{formatLocation(user.deliverlocation)}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="dp-card">
                <h2 className="dp-card-title">
                  <DirectionsCarOutlinedIcon />
                  Vehicle details
                </h2>
                <div className="dp-info-list">
                  <div className="dp-info-row">
                    <div className="dp-info-icon">
                      <DirectionsCarOutlinedIcon fontSize="small" />
                    </div>
                    <div>
                      <div className="dp-info-label">Type</div>
                      <div className="dp-info-value">{user.delivervehicle || 'Not set'}</div>
                    </div>
                  </div>
                  <div className="dp-info-row">
                    <div className="dp-info-icon">
                      <DirectionsCarOutlinedIcon fontSize="small" />
                    </div>
                    <div>
                      <div className="dp-info-label">Make / Model</div>
                      <div className="dp-info-value">
                        {[user.delivervehiclemake, user.delivervehiclemodel]
                          .filter(Boolean)
                          .join(' ') || 'Not set'}
                      </div>
                    </div>
                  </div>
                  <div className="dp-info-row">
                    <div className="dp-info-icon">
                      <PaletteOutlinedIcon fontSize="small" />
                    </div>
                    <div>
                      <div className="dp-info-label">Color</div>
                      <div className="dp-info-value">{user.delivervehiclecolor || 'Not set'}</div>
                    </div>
                  </div>
                  <div className="dp-info-row">
                    <div className="dp-info-icon">
                      <PinOutlinedIcon fontSize="small" />
                    </div>
                    <div>
                      <div className="dp-info-label">Plate Number</div>
                      <div className="dp-info-value">{user.delivervehiclenumber || 'Not set'}</div>
                    </div>
                  </div>
                  <div className="dp-info-row">
                    <div className="dp-info-icon">
                      <PinOutlinedIcon fontSize="small" />
                    </div>
                    <div>
                      <div className="dp-info-label">Taxi Driver Membership ID</div>
                      <div className="dp-info-value">{user.taxidriverid || 'Not set'}</div>
                    </div>
                  </div>
                </div>

                <div className={`dp-availability ${isActive ? 'active' : 'inactive'}`}>
                  <div className="dp-availability-text">
                    <strong>{isActive ? 'Available for assignments' : 'Currently unavailable'}</strong>
                    <span>
                      {isActive
                        ? 'Admins can assign you nearby orders'
                        : 'Turn on when you are ready to accept deliveries'}
                    </span>
                  </div>
                  <Switch
                    checked={isActive}
                    onChange={handleStatusToggle}
                    disabled={toggling}
                    color="success"
                  />
                </div>
              </div>

              <div className="dp-card" style={{ gridColumn: '1 / -1' }}>
                <h2 className="dp-card-title">
                  <LocalShippingOutlinedIcon />
                  Quick actions
                </h2>
                <div className="dp-actions-grid">
                  <Link to="/tables" className="dp-action-tile">
                    <TableViewOutlinedIcon />
                    <strong>Order list</strong>
                    <span>View assigned deliveries and pickup details</span>
                  </Link>
                  <Link to="/billing" className="dp-action-tile">
                    <ReceiptLongOutlinedIcon />
                    <strong>OTP confirmation</strong>
                    <span>Verify customer OTP after successful handoff</span>
                  </Link>
                  <Link to="/dashboard" className="dp-action-tile">
                    <DashboardOutlinedIcon />
                    <strong>Dashboard</strong>
                    <span>Overview and availability controls</span>
                  </Link>
                  {/* <a
                    href="https://jcgn.org/delivery-rates"
                    target="_blank"
                    rel="noreferrer"
                    className="dp-action-tile"
                  >
                    <OpenInNewOutlinedIcon />
                    <strong>Delivery rates</strong>
                    <span>Distance bands and commission on jcgn.org</span>
                  </a> */}
                  <a
                    href={`mailto:${SUPPORT_EMAIL}`}
                    className="dp-action-tile full"
                  >
                    <EmailOutlinedIcon />
                    <strong>Contact support</strong>
                    <span>Reach JCGN at {SUPPORT_EMAIL} if you need help with an order</span>
                  </a>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>

      <Footer />
    </DashboardLayout>
  );
}

export default Profile;
